import { useState } from "react";
import { autoCompleteBloqueado } from "../../utils/autofillNavegador";

const TIPOS_SEM_ASSISTENCIA = new Set([
  "color",
  "date",
  "datetime-local",
  "email",
  "file",
  "hidden",
  "month",
  "number",
  "password",
  "range",
  "tel",
  "time",
  "url",
  "week",
]);

export function obterAtributosAssistenciaTexto(tipo?: string) {
  const tipoNormalizado = String(tipo ?? "text").trim().toLowerCase();
  const campoEditavel = !TIPOS_SEM_ASSISTENCIA.has(tipoNormalizado);

  return {
    spellCheck: false,
    autoCorrect: "off" as const,
    autoCapitalize: "off" as const,
    lang: campoEditavel ? "pt-BR" : undefined,
  };
}

export function obterAtributosSemSugestoesNativas(tipo?: string) {
  return {
    autoComplete: autoCompleteBloqueado(tipo),
    "data-form-type": "other",
    "data-lpignore": "true",
    "data-1p-ignore": "true",
    "data-bwignore": "true",
  };
}

/**
 * O Chrome IGNORA `autocomplete="off"` em `<input type="password">`: o balão do
 * gerenciador de senhas (com as credenciais salvas) aparece de qualquer jeito,
 * porque ele identifica o campo pelo próprio `type`. A única forma confiável de
 * o navegador não reconhecer o campo é não usar `type="password"` — o input vira
 * `type="text"` e a máscara passa a ser feita pelo CSS `-webkit-text-security`.
 *
 * Só é seguro trocar quando o navegador suporta a propriedade; sem suporte o
 * campo ficaria com a senha à vista, então nesse caso mantemos `type="password"`
 * (o balão volta, mas a senha continua mascarada — segurança acima de estética).
 *
 * Suporte: Chrome/Edge/Safari há anos e Firefox a partir do 118.
 */
let suporteMascaraSenhaCss: boolean | null = null;

export function navegadorMascaraSenhaPorCss(): boolean {
  if (suporteMascaraSenhaCss === null) {
    suporteMascaraSenhaCss =
      typeof CSS !== "undefined" &&
      typeof CSS.supports === "function" &&
      CSS.supports("-webkit-text-security", "disc");
  }
  return suporteMascaraSenhaCss;
}

const TIPOS_SEM_BLOQUEIO_ATE_FOCO = new Set([
  "checkbox",
  "radio",
  "range",
  "color",
  "file",
  "hidden",
  "submit",
  "button",
  "reset",
]);

/**
 * O Chrome ignora autocomplete="off" para o próprio recurso nativo de
 * "Autofill" (endereços/contatos) — só autoComplete/data-lpignore não bastam
 * para impedir o balão de sugestão. Manter o campo readOnly até o primeiro
 * foco (e reativar no blur) evita que o navegador classifique o campo como
 * preenchível no momento em que decide exibir o balão.
 */
export function precisaBloqueioAteFoco(tipo?: string) {
  return !TIPOS_SEM_BLOQUEIO_ATE_FOCO.has(String(tipo ?? "text").trim().toLowerCase());
}

/**
 * Hook para inputs "crus" (fora do CampoTexto) aplicarem o mesmo bloqueio
 * de sugestão nativa do Chrome: `{...useBloqueioAteFoco()}` no `<input>`.
 */
export function useBloqueioAteFoco(tipo?: string) {
  const [emFoco, setEmFoco] = useState(false);
  const ativo = precisaBloqueioAteFoco(tipo);
  return {
    readOnly: ativo ? !emFoco : undefined,
    onFocus: () => { if (ativo) setEmFoco(true); },
    onBlur: () => { if (ativo) setEmFoco(false); },
  };
}
